/** Pure helpers for the player cross-session dashboard. Kept in `lib/` so they
 * can be unit-tested without pulling in React (vitest runs in node env). */

export type TrendTone = 'up' | 'down' | 'flat';

/** Below this absolute % change, a trend is considered effectively flat. */
export const TREND_FLAT_BAND_PCT = 5;

/**
 * Compare first vs last value as a percentage change. Returns null when there
 * aren't enough points or the baseline is 0 (avoids ±∞).
 *
 * The tone is purely directional — callers decide whether "up" means "good"
 * (e.g. distance) or is neutral (e.g. session duration).
 */
export function computeTrend(
  values: number[],
): { label: string; tone: TrendTone } | null {
  if (values.length < 2) return null;
  const first = values[0];
  const last = values[values.length - 1];
  if (first === 0) return null;
  const pct = ((last - first) / Math.abs(first)) * 100;
  const sign = pct > 0 ? '+' : '';
  const label = `${sign}${pct.toFixed(0)}%`;
  if (pct >= TREND_FLAT_BAND_PCT) return { label, tone: 'up' };
  if (pct <= -TREND_FLAT_BAND_PCT) return { label, tone: 'down' };
  return { label, tone: 'flat' };
}

/**
 * Round up to a "nice" decimal scale endpoint (1, 2, 5, 10, …) suitable for
 * y-axis ticks.
 */
export function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / pow;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * pow;
}
